import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../base/base.component';
import { AdminFeatureService } from '../../services/admin-feature/admin-feature.service';
import { ServiceService } from '../../services/service/service.service';
import { Feature } from '../../models/feature';

/** Subscription plan UI (price, features, visible toggle + copy link) */
export interface SubscriptionPlanItem {
  id: string;
  name: string;
  price: string;
  annualPrice: string;
  recurrency: number | null;
  priceType: string;
  features: Feature[];
  visible: boolean;
  customFeeRate: number | null;
  monthCount: number | null;
  createdAt?: string;
}

@Component({
  selector: 'app-admin-subscriptions',
  templateUrl: './admin-subscriptions.component.html',
  styleUrls: ['./admin-subscriptions.component.scss']
})
export class AdminSubscriptionsComponent extends BaseComponent implements OnInit {

  activeTab: 'features' | 'subscriptions' | 'domains' = 'subscriptions';

  features: Feature[] = [];
  loading = false;
  /** True from init so skeleton shows immediately on Abbonamenti tab instead of flashing "Crea il tuo primo abbonamento". */
  loadingSubscriptions = true;

  subscriptionPlans: SubscriptionPlanItem[] = [];

  editFeature: Feature | null = null;
  editFeatureOriginalCode: string = '';
  newFeature = { code: '', name: '' };

  editPlanId: string | null = null;
  addFeatureToPlanCode: string = '';

  isCustomSubscription = false;
  newSubscriptionCycleMonths: 6 | 12 | 24 = 12;
  isAnnualPriceManuallyEdited = false;
  newSubscription: { name: string; price: string; annualPrice: string; customFeeRate: string; monthCount: string } = {
    name: '', price: '', annualPrice: '', customFeeRate: '', monthCount: ''
  };
  newSubscriptionFeatures: Feature[] = [];
  addFeatureToNewSubscriptionCode: string = '';

  planSearch = '';
  planVisibility: 'all' | 'visible' | 'hidden' = 'all';
  planSort: 'date-newest' | 'date-oldest' | 'price-asc' | 'price-desc' | 'name' = 'date-newest';

  constructor(
    public adminFeatureService: AdminFeatureService,
    public serviceService: ServiceService,
    public serviceInjector: Injector,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(serviceInjector);
  }

  async ngOnInit() {
    this.activeTab = this.resolveTab();
    if (this.activeTab === 'subscriptions') {
      this.loadingSubscriptions = true; // keep skeleton visible until plans are loaded
    }
    await this.loadFeatures();
    if (this.activeTab === 'subscriptions') {
      await this.loadSubscriptionPlans();
    }
  }

  setTab(tab: 'features' | 'subscriptions' | 'domains') {
    this.activeTab = tab;
    void this.router.navigate(['/admins/subscriptions'], {
      queryParams: tab === 'subscriptions' ? {} : { tab },
      replaceUrl: true,
    });
    if (tab === 'subscriptions') {
      this.loadingSubscriptions = true; // show skeleton immediately when switching to tab
      this.loadSubscriptionPlans();
    }
  }

  get sectionTitle(): string {
    if (this.activeTab === 'features') return 'Feature';
    if (this.activeTab === 'domains') return 'Domini';
    return 'Abbonamenti';
  }

  get sectionDescription(): string {
    if (this.activeTab === 'features') {
      return 'Moduli che puoi associare a un piano di abbonamento.';
    }
    if (this.activeTab === 'domains') {
      return 'Record DNS Cloudflare collegati alle società.';
    }
    return 'Piani, prezzi e visibilità per le società sulla piattaforma.';
  }

  private resolveTab(): 'features' | 'subscriptions' | 'domains' {
    if (this.router.url.split('?')[0].endsWith('/domains')) {
      return 'domains';
    }
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'features' || tab === 'domains' || tab === 'subscriptions') {
      return tab;
    }
    return 'subscriptions';
  }

  async loadFeatures() {
    this.loading = true;
    const res = await this.adminFeatureService.getFeatures();
    this.loading = false;
    if (res.success && res.data) {
      this.features = res.data.map((f: any) => new Feature(f));
    } else {
      this.features = [];
    }
  }

  async saveNewFeature() {
    if (!this.newFeature.code?.trim() || !this.newFeature.name?.trim()) return;
    this.showSpinner();
    const res = await this.adminFeatureService.addFeature({
      code: this.newFeature.code.trim(),
      name: this.newFeature.name.trim()
    });
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage('Feature creata con successo');
      this.newFeature = { code: '', name: '' };
      await this.loadFeatures();
    } else {
      this.showErrorMessage(res.message || 'Errore durante la creazione');
    }
  }

  startEditFeature(f: Feature) {
    this.editFeature = { ...f };
    this.editFeatureOriginalCode = f.code;
  }

  cancelEditFeature() {
    this.editFeature = null;
    this.editFeatureOriginalCode = '';
  }

  async saveEditFeature() {
    if (!this.editFeature?.code || !this.editFeature.name?.trim()) return;
    this.showSpinner();
    const res = await this.adminFeatureService.updateFeature(this.editFeatureOriginalCode, {
      name: this.editFeature.name.trim(),
      code: this.editFeature.code.trim()
    });
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage('Feature aggiornata con successo');
      this.editFeature = null;
      this.editFeatureOriginalCode = '';
      await this.loadFeatures();
    } else {
      this.showErrorMessage(res.message || 'Errore durante l\'aggiornamento');
    }
  }

  async deleteFeature(f: Feature) {
    const result = await this.showQuestionMessage();
    if (!result) return;
    this.showSpinner();
    const res = await this.adminFeatureService.deleteFeature(f.code);
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage('Feature eliminata con successo');
      await this.loadFeatures();
    } else {
      this.showErrorMessage(res.message || 'Errore durante l\'eliminazione');
    }
  }

  startEditPlan(plan: SubscriptionPlanItem) {
    this.editPlanId = plan.id;
    this.addFeatureToPlanCode = '';
  }

  cancelEditPlan() {
    this.editPlanId = null;
    this.addFeatureToPlanCode = '';
  }

  getAvailableFeaturesForPlan(plan: SubscriptionPlanItem): Feature[] {
    const assigned = (plan.features ?? []).map(f => f.code);
    return this.features.filter(f => !assigned.includes(f.code));
  }

  getAvailableFeaturesForNewSubscription(): Feature[] {
    const assigned = this.newSubscriptionFeatures.map(f => f.code);
    return this.features.filter(f => !assigned.includes(f.code));
  }

  addFeatureToNewSubscription() {
    const code = this.addFeatureToNewSubscriptionCode;
    if (!code) return;
    const feat = this.features.find(f => f.code === code);
    if (feat) {
      this.newSubscriptionFeatures = [...this.newSubscriptionFeatures, feat];
      this.addFeatureToNewSubscriptionCode = '';
    }
  }

  removeFeatureFromNewSubscription(code: string) {
    this.newSubscriptionFeatures = this.newSubscriptionFeatures.filter(f => f.code !== code);
  }

  /** Keeps period price in sync with monthly x selected cycle months while creating a plan. */
  onNewSubscriptionMonthlyPriceChange(value: string): void {
    this.newSubscription.price = String(value ?? '');
    this.syncNewSubscriptionPeriodPrice();
  }

  onNewSubscriptionCycleMonthsChange(months: 6 | 12 | 24): void {
    this.newSubscriptionCycleMonths = months;
    this.syncNewSubscriptionPeriodPrice();
  }

  onNewSubscriptionAnnualPriceChange(value: string): void {
    this.newSubscription.annualPrice = String(value ?? '');
    this.isAnnualPriceManuallyEdited = true;
  }

  useAutoCalculatedAnnualPrice(): void {
    this.isAnnualPriceManuallyEdited = false;
    this.syncNewSubscriptionPeriodPrice();
  }

  private syncNewSubscriptionPeriodPrice(): void {
    if (this.isAnnualPriceManuallyEdited) {
      return;
    }

    const raw = String(this.newSubscription.price ?? '').trim().replace(',', '.');
    if (raw === '') {
      this.newSubscription.annualPrice = '';
      return;
    }

    const monthlyPrice = parseFloat(raw);
    if (Number.isNaN(monthlyPrice)) {
      this.newSubscription.annualPrice = '';
      return;
    }

    const totalPeriodPrice = monthlyPrice * this.newSubscriptionCycleMonths;
    this.newSubscription.annualPrice = totalPeriodPrice.toFixed(2);
  }

  getSelectedCycleLabel(): string {
    return `${this.newSubscriptionCycleMonths} mesi`;
  }

  getCalculatedCycleAmountLabel(): string {
    const raw = String(this.newSubscription.price ?? '').trim().replace(',', '.');
    if (raw === '') {
      return '0.00';
    }
    const n = parseFloat(raw);
    if (Number.isNaN(n)) {
      return '0.00';
    }
    return (n * this.newSubscriptionCycleMonths).toFixed(2);
  }

  async addFeatureToPlan(plan: SubscriptionPlanItem) {
    const code = this.addFeatureToPlanCode;
    if (!code) return;
    this.showSpinner();
    const res = await this.adminFeatureService.addFeatureToService(plan.id, code);
    this.hideSpinner();
    if (res.success && res.data) {
      this.showSuccessMessage('Feature aggiunta');
      // Use feature from local list (has name) - API may only return code
      const feat = this.features.find(f => f.code === code) ?? new Feature(res.data);
      plan.features = [...(plan.features ?? []), feat];
      this.addFeatureToPlanCode = '';
    } else {
      this.showErrorMessage(res.message || 'Errore');
    }
  }

  async removeFeatureFromPlan(plan: SubscriptionPlanItem, featureCode: string) {
    this.showSpinner();
    const res = await this.adminFeatureService.removeFeatureFromService(plan.id, featureCode);
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage('Feature rimossa');
      plan.features = (plan.features ?? []).filter(f => f.code !== featureCode);
    } else {
      this.showErrorMessage(res.message || 'Errore');
    }
  }

  async createSubscription() {
    if (!this.newSubscription.name?.trim() || !this.newSubscription.price?.trim()) return;
    this.showSpinner();
    try {
      const parsedFeeRate = this.isCustomSubscription ? parseFloat(this.newSubscription.customFeeRate) : NaN;
      const parsedMonthCount = this.isCustomSubscription ? parseInt(this.newSubscription.monthCount, 10) : NaN;
      const res = await this.adminFeatureService.createService({
        name: this.newSubscription.name.trim(),
        monthlyPrice: this.newSubscription.price.trim(),
        annualPrice: this.newSubscription.annualPrice?.trim(),
        recurrency: this.newSubscriptionCycleMonths,
        customFeeRate: !isNaN(parsedFeeRate) ? parsedFeeRate : null,
        monthCount: !isNaN(parsedMonthCount) ? parsedMonthCount : null
      });
      if (res.success && res.data) {
        const serviceId = res.data.id;
        for (const feat of this.newSubscriptionFeatures) {
          await this.adminFeatureService.addFeatureToService(serviceId, feat.code);
        }
      this.showSuccessMessage('Abbonamento creato con successo');
      const data = res.data as { id: string; name: string; price?: string; monthlyPrice?: string; annualPrice?: string; recurrency?: number; priceType?: string; customFeeRate?: number; monthCount?: number };
      this.subscriptionPlans = [
        {
          id: serviceId,
          name: data.name,
          price: data.monthlyPrice ?? data.price ?? '',
          annualPrice: data.annualPrice ?? '',
          recurrency: data.recurrency ?? this.newSubscriptionCycleMonths,
          priceType: data.priceType ?? 'Monthly',
          features: [...this.newSubscriptionFeatures],
          visible: true,
          customFeeRate: data.customFeeRate ?? (!isNaN(parsedFeeRate) ? parsedFeeRate : null),
          monthCount: data.monthCount ?? (!isNaN(parsedMonthCount) ? parsedMonthCount : null),
          createdAt: new Date().toISOString()
        },
        ...this.subscriptionPlans
      ];
      this.newSubscription = { name: '', price: '', annualPrice: '', customFeeRate: '', monthCount: '' };
      this.newSubscriptionCycleMonths = 12;
      this.isAnnualPriceManuallyEdited = false;
      this.isCustomSubscription = false;
      this.newSubscriptionFeatures = [];
      this.addFeatureToNewSubscriptionCode = '';
    } else {
      this.showErrorMessage(res.message || 'Errore durante la creazione');
    }
    } finally {
      this.hideSpinner();
    }
  }

  /** Load subscription plans for "Abbonamenti" tab with price, features, visible toggle. */
  async loadSubscriptionPlans() {
    this.loadingSubscriptions = true;
    try {
      const svc = await this.serviceService.getAllServices(false);
      const plans: SubscriptionPlanItem[] = [];
      for (const s of svc) {
        const res = await this.adminFeatureService.getServiceFeatures(s.id);
        const feats = (res.success && res.data) ? res.data.map((f: any) => new Feature(f)) : [];
        plans.push({
          id: s.id,
          name: s.name,
          price: s.price ?? '',
          annualPrice: s.annualPrice ?? '',
          recurrency: (s as any).recurrency ?? null,
          priceType: s.priceType ?? 'Monthly',
          features: feats,
          visible: (s as { visible?: boolean }).visible ?? true,
          customFeeRate: (s as any).customFeeRate ?? null,
          monthCount: (s as any).monthCount ?? null,
          createdAt: (s as { createdAt?: string }).createdAt
        });
      }
      this.subscriptionPlans = plans;
    } finally {
      this.loadingSubscriptions = false;
    }
  }

  get filteredSubscriptionPlans(): SubscriptionPlanItem[] {
    const query = this.planSearch.trim().toLowerCase();
    let plans = this.subscriptionPlans.filter((plan) => {
      if (this.planVisibility === 'visible' && !plan.visible) return false;
      if (this.planVisibility === 'hidden' && plan.visible) return false;
      if (query && !plan.name.toLowerCase().includes(query)) return false;
      return true;
    });

    return plans.sort((a, b) => {
      switch (this.planSort) {
        case 'price-asc':
          return this.planPriceValue(a) - this.planPriceValue(b);
        case 'price-desc':
          return this.planPriceValue(b) - this.planPriceValue(a);
        case 'date-oldest':
          return this.planDateValue(a) - this.planDateValue(b);
        case 'name':
          return a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });
        case 'date-newest':
        default:
          return this.planDateValue(b) - this.planDateValue(a);
      }
    });
  }

  private planPriceValue(plan: SubscriptionPlanItem): number {
    const n = parseFloat(String(plan.price ?? '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
  }

  private planDateValue(plan: SubscriptionPlanItem): number {
    const t = plan.createdAt ? Date.parse(plan.createdAt) : 0;
    return Number.isNaN(t) ? 0 : t;
  }

  getCustomSubscriptionLink(plan: SubscriptionPlanItem): string {
    const base = window.location.origin;
    return `${base}/register/companies?idService=${plan.id}`;
  }

  /** Recurring total to monthly equivalent (e.g. "96" every 12 months -> "8.00"). */
  getAnnualPricePerMonth(annualPrice: string, recurrency: number | null = 12): string {
    const n = parseFloat(annualPrice);
    const months = recurrency && recurrency > 0 ? recurrency : 12;
    return (Number.isNaN(n) ? 0 : n / months).toFixed(2);
  }

  copyCustomSubscriptionLink(plan: SubscriptionPlanItem) {
    const link = this.getCustomSubscriptionLink(plan);
    navigator.clipboard.writeText(link).then(() => {
      this.showSuccessMessage('Link copiato negli appunti');
    }).catch(() => {
      this.showErrorMessage('Impossibile copiare il link');
    });
  }

  async onSubscriptionVisibleChange(plan: SubscriptionPlanItem) {
    this.showSpinner();
    const res = await this.adminFeatureService.updateService(plan.id, { visible: plan.visible });
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage(plan.visible ? 'Abbonamento reso visibile' : 'Abbonamento nascosto');
    } else {
      plan.visible = !plan.visible;
      this.showErrorMessage(res.message || 'Errore durante l\'aggiornamento');
    }
  }

  togglePlanVisible(plan: SubscriptionPlanItem) {
    plan.visible = !plan.visible;
    this.onSubscriptionVisibleChange(plan);
  }

  async deleteSubscription(plan: SubscriptionPlanItem) {
    const result = await this.showQuestionMessage();
    if (!result) return;
    this.showSpinner();
    const res = await this.adminFeatureService.deleteService(plan.id);
    this.hideSpinner();
    if (res.success) {
      this.showSuccessMessage('Abbonamento eliminato');
      if (this.editPlanId === plan.id) {
        this.cancelEditPlan();
      }
      this.subscriptionPlans = this.subscriptionPlans.filter(p => p.id !== plan.id);
    } else {
      this.showErrorMessage(res.message || 'Errore durante l\'eliminazione');
    }
  }
}
